import { useState } from 'react';
import { TFunction } from 'i18next';

import { isOwner, taskStatusByCode } from 'entities';

import { Tabs, Column } from 'components/Blocks';

import { TasksComments } from './Comments';
import { TasksFiles } from './Files';
import { TaskLinks } from './Links';

import { useProjectProfile } from '../../../context';
import { useTaskModal } from '../../context.taskModal';

import { validateHasLink } from 'utils/link';
import { useUser } from 'hooks';

const tabToComponent = {
  Comments: TasksComments,
  Files: TasksFiles,
  Links: TaskLinks,
} as const;

export function TaskUserInteractions() {
  const {
    project: { owner },
    projectUsers,
    fullBlocked,
  } = useProjectProfile();

  const loggedUser = useUser();

  const { task } = useTaskModal();

  const [tab, setTab] = useState('Comments');

  const Component = tabToComponent[tab as keyof typeof tabToComponent];

  const canEdit =
    [
      owner.id === loggedUser.id,
      isOwner(task, loggedUser),
      task.subTasks.some((subTask) => subTask.owner.id === loggedUser.id),
      ...(tab === 'Comments'
        ? [
            !!projectUsers[loggedUser.id],

            !!Object.values(projectUsers).find(
              ({ team }) => loggedUser.team.id === team && loggedUser.type === 'LEADER',
            ),
          ]
        : []),
    ].some(Boolean) &&
    !taskStatusByCode[task.status].blocks.fullBlock &&
    !fullBlocked;

  return (
    <Column>
      <Tabs
        onTabChange={setTab}
        value={tab}
        type="highlighted"
        tabListClassName="-ml-2"
        tabs={[
          { value: 'Comments', getLabel: (t: TFunction) => t('comments') },
          {
            value: 'Files',
            getLabel: (t: TFunction) => t('files'),
            hide: task.comments?.every((c) => !c.files?.length) && !task.files?.length,
          },
          {
            value: 'Links',
            getLabel: (t: TFunction) => t('links'),
            hide: !task.comments?.find(({ text }) => validateHasLink(text)),
          },
        ]}
      />

      <Component canEdit={canEdit} />
    </Column>
  );
}
